import { useCallback, useEffect } from "react";

import { atom, useAtom } from "jotai";
import {
  atomFamily,
  atomWithStorage,
  useAtomCallback,
  useAtomValue,
  useUpdateAtom,
} from "jotai/utils";
import deepEqual from "fast-deep-equal";

import { DataFile } from "interfaces";

import { useFetchColumnProfile } from "./column-insights";

import { fileColumns, useGetFileColumns } from "./file-api";
import { useGetColumnType } from "./column-types";

interface Spec {
  fileId: string;
  column: string;
}

export const columnRetainStatus = atomFamily(
  ({ fileId, column }: Spec) =>
    atomWithStorage<"keep" | "drop">(
      `data-files/column-retain-status-v2/${fileId}/${column}`,
      null
    ),
  deepEqual
);

const getInitDropStatus = (stats: DataFile.ColumnStatsInterface) => {
  if (stats.count <= 1) {
    return "drop";
  }
  if (stats["unique-count"] <= 1) {
    return "drop";
  }
  if (stats["count"] / stats["size"] <= 0.3) {
    return "drop";
  }
  return "keep";
};

export const useInitColumnStatus = (fileId: string, column: string) => {
  const colType = useGetColumnType(fileId, column);
  const { data } = useFetchColumnProfile(fileId, column, colType);

  const [status, setStatus] = useAtom(columnRetainStatus({ fileId, column }));
  useEffect(() => {
    if (!status && data) {
      setStatus(getInitDropStatus(data.stats));
    }
  }, [status, setStatus, data]);
};

export const useDropColumn = (fileId: string, column: string) => {
  const [_, updateDropStatus] = useAtom(columnRetainStatus({ fileId, column }));
  return () => updateDropStatus("drop");
};

export const useGetDropCallback = (fileId: string) =>
  useAtomCallback(
    useCallback(
      (get, set, column) => {
        console.log(column);
        if (column) {
          const currentValue = get(columnRetainStatus({ fileId, column }));
          console.log(currentValue);
          if (currentValue !== "drop") {
            set(columnRetainStatus({ fileId, column }), "drop");
          }
        }
      },
      [fileId]
    )
  );

export const useGetDropMultipleColumns = (fileId: string) =>
  useUpdateAtom(droppedColumns(fileId));

export const useRetainColumn = (fileId: string, column: string) => {
  const [_, updateDropStatus] = useAtom(columnRetainStatus({ fileId, column }));
  return () => updateDropStatus("keep");
};

export const useGetRetainCallback = (fileId: string) =>
  useAtomCallback(
    useCallback(
      (get, set, column) => {
        if (column) {
          const currentValue = get(columnRetainStatus({ fileId, column }));
          if (currentValue !== "keep") {
            set(columnRetainStatus({ fileId, column }), "keep");
          }
        }
      },
      [fileId]
    )
  );

export const useGetRetainMultipleColumns = (fileId: string) =>
  useUpdateAtom(retainedColumns(fileId));

export const droppedColumns = atomFamily((fileId: string) =>
  atom<string[], string[]>(
    (get) => {
      const columns = get(fileColumns(fileId));
      return columns.filter(
        (c) => get(columnRetainStatus({ fileId, column: c })) === "drop"
      );
    },
    (get, set, colsToDrop) => {
      const columns = get(fileColumns(fileId));
      colsToDrop?.forEach((column) => {
        set(columnRetainStatus({ fileId, column }), "drop");
      });
      columns
        .filter((c) => !colsToDrop.includes(c))
        .forEach((column) => {
          set(columnRetainStatus({ fileId, column }), "keep");
        });
    }
  )
);

export const useDroppedColumnsForFile = (fileId: string) =>
  useAtom(droppedColumns(fileId));

export const useGetDroppedColumns = (fileId: string) =>
  useAtomValue(droppedColumns(fileId));

export const retainedColumns = atomFamily((fileId: string) =>
  atom<string[], string[]>(
    (get) => {
      const columns = get(fileColumns(fileId));
      return columns.filter(
        (c) => get(columnRetainStatus({ fileId, column: c })) === "keep"
      );
    },
    (get, set, colsToDrop) => {
      const columns = get(fileColumns(fileId));
      colsToDrop?.forEach((column) => {
        set(columnRetainStatus({ fileId, column }), "keep");
      });
      columns
        .filter((c) => !colsToDrop.includes(c))
        .forEach((column) => {
          set(columnRetainStatus({ fileId, column }), "keep");
        });
    }
  )
);

export const useRetainedColumnsForFile = (fileId: string) =>
  useAtom(retainedColumns(fileId));

export const useGetRetainedColumns = (fileId: string) =>
  useAtomValue(retainedColumns(fileId));

export const useRetainAllColumns = (fileId: string, columns?: string[]) => {
  const fileColumns = useGetFileColumns(fileId);
  const [, updateRetainedColumns] = useAtom(retainedColumns(fileId));
  return useCallback(() => {
    updateRetainedColumns(columns || fileColumns);
  }, [columns, fileColumns, updateRetainedColumns]);
};
